import { Camera, CheckCircle, Eye, Tags } from "lucide-react";
import { StructuralAssessment } from "@/services/backend/htz/inspection/types/structural-assessment";
import { useMemo } from "react";
import { cn } from "@/shared/lib/utils";
import { VisualAssessment } from "@/services/backend/htz/inspection/types/visual-assessment";
import { Photo } from "@/services/backend/htz/inspection/types/photo";
import { MicrobialAssessment } from "@/services/backend/htz/inspection/types/microbial-assessment";
import { Result } from "@/services/backend/htz/inspection/components/inspection-result";

/**
 * ComponentAssessments are the assessments of the components of an air
 * handling unit, e.g. those of a work order position.
 */
export interface ComponentAssessments {
  structuralAssessments: StructuralAssessment[];
  visualAssessments: VisualAssessment[];
  microbialAssessments: MicrobialAssessment[];
  photos: Photo[];
}

/*
 * Inspection
 */
export function InspectionIndicator({
  componentId,
  assessments,
}: {
  componentId: string;
  assessments: ComponentAssessments;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {assessments.structuralAssessments
        .filter((sa) => sa.componentId === componentId)
        .map((a) => (
          <StructuralAssessmentIndicator assessment={a} key={a.id} />
        ))}
      {assessments.visualAssessments
        .filter((va) => va.componentId === componentId)
        .map((a) => (
          <VisualAssessmentIndicator assessment={a} key={a.id} />
        ))}
      {assessments.microbialAssessments
        .filter((ma) => ma.componentId === componentId)
        .map((a) => (
          <MicrobialAssessmentIndicator assessment={a} key={a.id} />
        ))}
      {assessments.photos
        .filter((p) => p.componentId === componentId)
        .map((p) => (
          <PhotoIndicator photo={p} key={p.id} />
        ))}
    </div>
  );
}

function StructuralAssessmentIndicator({
  assessment,
}: {
  assessment: StructuralAssessment;
}) {
  const className = useMemo(() => {
    switch (assessment.result) {
      case Result.WARNING:
        return "bg-yellow-300 border-yellow-300";
      case Result.CRITICAL:
        return "bg-red-500 border-red-500";
      default:
        return "bg-background";
    }
  }, [assessment]);

  return (
    <div
      className={cn(
        "inline-flex h-6 min-w-6 items-center rounded-md border px-1 text-[8pt]",
        className,
      )}
    >
      <CheckCircle className="h-3 w-3" />
    </div>
  );
}

function VisualAssessmentIndicator({
  assessment,
}: {
  assessment: VisualAssessment;
}) {
  const className = useMemo(() => {
    switch (assessment.result) {
      case Result.WARNING:
        return "bg-yellow-300 border-yellow-300";
      case Result.CRITICAL:
        return "bg-red-500 border-red-500";
      default:
        return "bg-background";
    }
  }, [assessment]);

  return (
    <div
      className={cn(
        "inline-flex h-6 min-w-6 items-center justify-center rounded-md border px-1 text-[8pt]",
        className,
      )}
    >
      <Eye className="h-4 w-4" />
    </div>
  );
}

function MicrobialAssessmentIndicator({
  assessment,
}: {
  assessment: MicrobialAssessment;
}) {
  const className = useMemo(() => {
    switch (assessment.result) {
      case Result.WARNING:
        return "bg-yellow-300 border-yellow-300";
      case Result.CRITICAL:
        return "bg-red-500 border-red-500";
      default:
        return "bg-background";
    }
  }, [assessment]);

  // this indicates that there is no actual sample taken
  if (assessment.sampleId === null) {
    return null;
  }

  return (
    <div
      className={cn(
        "bg-background inline-flex h-6 min-w-6 items-center justify-center rounded-md border px-1 text-[8pt]",
        className,
      )}
    >
      <Tags className="h-4 w-4" />
    </div>
  );
}

function PhotoIndicator({ photo }: { photo: Photo }) {
  // this indicates that there is no actual photo taken
  if (photo.fileId === null) {
    return null;
  }

  return (
    <div
      className={cn(
        "bg-background inline-flex h-6 min-w-6 items-center justify-center rounded-md border px-1 text-[8pt]",
      )}
    >
      <Camera className="h-4 w-4" />
    </div>
  );
}
